import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  createTask,
  updateTask,
  addDependency,
  removeDependency,
  type Task,
  type TaskDependency,
  type Milestone,
} from '../../packages/api-client/pm-api';
import {
  createPhase,
  updatePhase,
  reorderPhases,
  deletePhase,
  addMilestoneDependency,
  removeMilestoneDependency,
  type Phase,
  type ScheduledTask,
} from '../../packages/api-client/plan-api';
import { toDateInputValue } from '../../utils/date';

interface PhaseRollupEntry {
  start?: string | null;
  end?: string | null;
  taskCount: number;
  doneCount: number;
}

interface Props {
  projectId: string;
  phases: Phase[];
  tasks: Task[];
  milestones?: Milestone[];
  dependsOn: Record<string, TaskDependency[]>;
  scheduleByTask: Record<string, ScheduledTask>;
  phaseRollups?: Map<string, PhaseRollupEntry>;
  /** Called after any edit; lists the tasks whose dependencies changed, if any. */
  onChanged: (depsChangedFor?: string[]) => void;
}

function PhaseHeader({
  phase,
  phases,
  projectId,
  rollup,
  onChanged,
}: {
  phase: Phase;
  phases: Phase[];
  projectId: string;
  rollup?: PhaseRollupEntry;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(phase.name);

  useEffect(() => { setName(phase.name); }, [phase.name]);

  const sorted = [...phases].sort((a, b) => a.position - b.position);
  const idx = sorted.findIndex(p => p.id === phase.id);

  async function saveName() {
    setEditing(false);
    if (name === phase.name) return;
    try {
      await updatePhase(phase.id, { name });
      onChanged();
    } catch {
      toast.error('Failed to rename phase');
    }
  }

  async function moveUp() {
    if (idx <= 0) return;
    const newOrder = sorted.map(p => p.id);
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    try {
      await reorderPhases(projectId, newOrder);
      onChanged();
    } catch {
      toast.error('Failed to reorder phases');
    }
  }

  async function moveDown() {
    if (idx >= sorted.length - 1) return;
    const newOrder = sorted.map(p => p.id);
    [newOrder[idx + 1], newOrder[idx]] = [newOrder[idx], newOrder[idx + 1]];
    try {
      await reorderPhases(projectId, newOrder);
      onChanged();
    } catch {
      toast.error('Failed to reorder phases');
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete phase "${phase.name}"? Tasks will become unphased.`)) return;
    try {
      await deletePhase(phase.id);
      onChanged();
    } catch {
      toast.error('Failed to delete phase');
    }
  }

  return (
    <div className="plan-phase-header">
      <span className="plan-phase-dot" style={{ background: phase.color }} aria-hidden="true" />
      {editing ? (
        <input
          autoFocus
          aria-label={`Phase name for ${phase.name}`}
          className="form-input"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') { setName(phase.name); setEditing(false); } }}
          style={{ flex: 1 }}
        />
      ) : (
        <>
          <span
            role="button"
            tabIndex={0}
            data-testid="phase-header-name"
            aria-label={`Rename phase ${phase.name}`}
            className="plan-phase-name"
            onClick={() => setEditing(true)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
          >
            {phase.name}
          </span>
          {rollup ? (
            <span className="plan-phase-rollup">
              {rollup.start && rollup.end ? `${toDateInputValue(rollup.start)} – ${toDateInputValue(rollup.end)} · ` : ''}{rollup.doneCount}/{rollup.taskCount}
            </span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
        </>
      )}
      <button type="button" className="btn btn-ghost btn-sm plan-icon-btn" onClick={moveUp} disabled={idx === 0} title="Move up" aria-label={`Move phase ${phase.name} up`}>↑</button>
      <button type="button" className="btn btn-ghost btn-sm plan-icon-btn" onClick={moveDown} disabled={idx === sorted.length - 1} title="Move down" aria-label={`Move phase ${phase.name} down`}>↓</button>
      <button type="button" className="btn btn-ghost btn-sm plan-icon-btn plan-danger" onClick={handleDelete} title="Delete phase" aria-label={`Delete phase ${phase.name}`}>✕</button>
    </div>
  );
}

function DepsControl({
  task,
  allTasks,
  deps,
  onChanged,
}: {
  task: Task;
  allTasks: Task[];
  deps: TaskDependency[];
  onChanged: (depsChangedFor?: string[]) => void;
}) {
  const others = allTasks.filter(t => t.id !== task.id);
  const depIds = new Set(deps.map(d => d.depends_on));

  async function toggle(otherId: string) {
    if (depIds.has(otherId)) {
      const edge = deps.find(d => d.depends_on === otherId);
      if (!edge) return;
      try {
        await removeDependency(task.id, edge.id);
        onChanged([task.id]);
      } catch {
        toast.error('Failed to remove dependency');
      }
    } else {
      try {
        await addDependency(task.id, otherId);
        onChanged([task.id]);
      } catch {
        toast.error('Failed to add dependency');
      }
    }
  }

  if (others.length === 0) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>—</span>;

  return (
    <details style={{ fontSize: 'var(--font-size-xs)' }}>
      <summary style={{ cursor: 'pointer', color: deps.length > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
        {deps.length > 0 ? `${deps.length} dep${deps.length !== 1 ? 's' : ''}` : 'Deps'}
      </summary>
      <div style={{ position: 'absolute', zIndex: 10, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', minWidth: 160, boxShadow: 'var(--shadow-md)' }}>
        {others.map(other => (
          <label key={other.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={depIds.has(other.id)}
              onChange={() => { toggle(other.id); }}
            />
            <span>{other.title}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function MilestoneGatesControl({
  task,
  projectId,
  milestones,
  onChanged,
}: {
  task: Task;
  projectId: string;
  milestones: Milestone[];
  onChanged: () => void;
}) {
  // Local optimistic state for milestone gates, scoped to this component instance
  const [gated, setGated] = useState<Set<string>>(() => new Set());

  if (milestones.length === 0) return null;

  async function toggle(milestoneId: string) {
    if (gated.has(milestoneId)) {
      try {
        await removeMilestoneDependency(projectId, milestoneId, task.id);
        setGated(prev => { const next = new Set(prev); next.delete(milestoneId); return next; });
        onChanged();
      } catch {
        toast.error('Failed to remove milestone gate');
      }
    } else {
      try {
        await addMilestoneDependency(projectId, milestoneId, task.id);
        setGated(prev => new Set([...prev, milestoneId]));
        onChanged();
      } catch {
        toast.error('Failed to add milestone gate');
      }
    }
  }

  return (
    <details style={{ fontSize: 'var(--font-size-xs)', marginTop: 4 }}>
      <summary style={{ cursor: 'pointer', color: gated.size > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
        {gated.size > 0 ? `${gated.size} milestone gate${gated.size !== 1 ? 's' : ''}` : 'Milestone gates'}
      </summary>
      <div style={{ position: 'absolute', zIndex: 11, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2)', minWidth: 180, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>Depends on milestone</div>
        {milestones.map(ms => (
          <label key={ms.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={gated.has(ms.id)}
              onChange={() => { toggle(ms.id); }}
            />
            <span>{ms.name}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function TaskRow({
  task,
  phases,
  allTasks,
  deps,
  scheduled,
  projectId,
  milestones,
  onChanged,
}: {
  task: Task;
  phases: Phase[];
  allTasks: Task[];
  deps: TaskDependency[];
  scheduled?: ScheduledTask;
  projectId: string;
  milestones?: Milestone[];
  onChanged: (depsChangedFor?: string[]) => void;
}) {
  const [durVal, setDurVal] = useState(task.duration_days != null ? String(task.duration_days) : '');

  async function handleDurBlur() {
    const parsed = Number(durVal);
    if (!durVal || isNaN(parsed) || parsed === task.duration_days) return;
    try {
      await updateTask(task.id, { duration_days: parsed });
      onChanged();
    } catch {
      toast.error('Failed to update duration');
    }
  }

  async function handlePhaseChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value;
    try {
      await updateTask(task.id, { phase_id: val || undefined });
      onChanged();
    } catch {
      toast.error('Failed to update phase');
    }
  }

  return (
    <tr>
      <td>
        <Link to={`/tasks/${task.id}`} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {task.title}
        </Link>
      </td>
      <td>
        <input
          type="number"
          aria-label={`Duration for ${task.title}`}
          value={durVal}
          min={1}
          onChange={e => setDurVal(e.target.value)}
          onBlur={handleDurBlur}
          className="form-input"
          style={{ width: 64 }}
        />
      </td>
      <td>
        <select
          aria-label={`Phase for ${task.title}`}
          value={task.phase_id ?? ''}
          onChange={handlePhaseChange}
          className="form-input"
          style={{ width: '100%' }}
        >
          <option value="">Unphased</option>
          {phases.map(ph => (
            <option key={ph.id} value={ph.id}>{ph.name}</option>
          ))}
        </select>
      </td>
      <td style={{ position: 'relative' }}>
        <DepsControl task={task} allTasks={allTasks} deps={deps} onChanged={onChanged} />
        {milestones && milestones.length > 0 && (
          <MilestoneGatesControl task={task} projectId={projectId} milestones={milestones} onChanged={onChanged} />
        )}
      </td>
      <td style={{ fontSize: 'var(--font-size-xs)', color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
        {scheduled ? (
          <>
            <span>{toDateInputValue(scheduled.start)} → {toDateInputValue(scheduled.due)}</span>
            {' '}
            {scheduled.is_critical ? (
              <span className="plan-badge plan-badge--critical">Critical</span>
            ) : scheduled.slack_days != null ? (
              <span className="plan-badge plan-badge--slack">{scheduled.slack_days}d slack</span>
            ) : null}
          </>
        ) : (
          <span style={{ color: 'var(--color-text-faint)' }}>—</span>
        )}
      </td>
    </tr>
  );
}

export function PlanTable({ projectId, phases, tasks, milestones, dependsOn, scheduleByTask, phaseRollups, onChanged }: Props) {
  const sortedPhases = [...phases].sort((a, b) => a.position - b.position);

  const tasksByPhase = tasks.reduce<Record<string, Task[]>>((acc, t) => {
    const key = t.phase_id ?? '__unphased__';
    (acc[key] ??= []).push(t);
    return acc;
  }, {});

  async function handleAddPhase() {
    try {
      await createPhase(projectId, { name: 'New phase' });
      onChanged();
    } catch {
      toast.error('Failed to add phase');
    }
  }

  async function handleAddTask(phaseId: string | null) {
    try {
      await createTask(projectId, { title: 'New task', phase_id: phaseId ?? undefined });
      onChanged();
    } catch {
      toast.error('Failed to add task');
    }
  }

  function renderSection(phaseId: string | null, label: React.ReactNode, phase?: Phase) {
    const sectionTasks = phaseId ? (tasksByPhase[phaseId] ?? []) : (tasksByPhase['__unphased__'] ?? []);
    return (
      <div key={phaseId ?? '__unphased__'} style={{ marginBottom: 'var(--space-3)' }}>
        {phase ? (
          <PhaseHeader phase={phase} phases={phases} projectId={projectId} rollup={phaseRollups?.get(phase.id)} onChanged={onChanged} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            {label}
          </div>
        )}
        {sectionTasks.length > 0 && (
          <table className="plan-table">
            <caption className="sr-only">{phase ? `Tasks in phase ${phase.name}` : 'Unphased tasks'}</caption>
            <colgroup>
              <col />
              <col style={{ width: 70 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 60 }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Task</th>
                <th scope="col">Days</th>
                <th scope="col">Phase</th>
                <th scope="col">Deps</th>
                <th scope="col">Schedule</th>
              </tr>
            </thead>
            <tbody>
              {sectionTasks.map(t => (
                <TaskRow
                  key={t.id}
                  task={t}
                  phases={phases}
                  allTasks={tasks}
                  deps={dependsOn[t.id] ?? []}
                  scheduled={scheduleByTask[t.id]}
                  projectId={projectId}
                  milestones={milestones}
                  onChanged={onChanged}
                />
              ))}
            </tbody>
          </table>
        )}
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => handleAddTask(phaseId)}
          style={{ marginTop: 'var(--space-1)', fontSize: 'var(--font-size-xs)' }}
        >
          + Add task
        </button>
      </div>
    );
  }

  const unphasedTasks = tasksByPhase['__unphased__'] ?? [];

  return (
    <div>
      {sortedPhases.map(ph => renderSection(ph.id, ph.name, ph))}
      {unphasedTasks.length > 0 && renderSection(null, 'Unphased')}
      <button
        className="btn btn-ghost btn-sm"
        onClick={handleAddPhase}
        style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-xs)', borderStyle: 'dashed' }}
      >
        + Add phase
      </button>
    </div>
  );
}
